/**
 * Phase 1.9 — Contacts module index, v2.
 *
 * Operations → Contacts surface, served when layout_v2 === 'v2'. Lists
 * every correspondent as a sortable table; the Add Contact dialog
 * appends new rows. Mirrors v1 in features but uses the v1.9
 * primitives — KpiCard, FilterBar, Card chrome, EmptyState — for the
 * look-and-feel Brian wants from the new layout phase.
 *
 * Three small KPIs at top, FilterBar with search + tag filter, then a
 * single table. Card grid wasn't a fit here — contacts are inherently
 * a list of small structured records, not assets with thumbnails.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Users, Plus, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, EmptyState, FilterBar, KpiCard } from '@/components/v2';
import { usePageHeader } from '@/components/v2/layout/Header';
import { AddContactDialog } from '@/components/v2/dialogs/AddContactDialog';
import { authFetch } from '@/lib/auth';
import type { Contact, Tag } from '@shared/types';

export function ContactsPageV2(): JSX.Element {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [tagFilter, setTagFilter] = useState<string>('all');
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  usePageHeader(
    'Contacts',
    <Button
      size="sm"
      onClick={() => setAddOpen(true)}
      className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
    >
      <Plus className="h-4 w-4" />
      Add contact
    </Button>,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    async function load(): Promise<void> {
      try {
        const [contactsRes, tagsRes] = await Promise.all([
          authFetch<{ items: Contact[] }>('/api/contacts'),
          authFetch<{ items: Tag[] }>('/api/docs/taxonomy/tags').catch(() => ({ items: [] as Tag[] })),
        ]);
        if (!cancelled) {
          setContacts(contactsRes.items ?? []);
          setTags(tagsRes.items ?? []);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load contacts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  const tagsById = useMemo(() => {
    const m = new Map<string, Tag>();
    for (const t of tags) m.set(t.id, t);
    return m;
  }, [tags]);

  const filtered = useMemo<Contact[]>(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => {
      if (q) {
        const hay = `${c.company} ${c.first_name ?? ''} ${c.last_name ?? ''} ${c.email ?? ''} ${c.phone ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (tagFilter !== 'all' && !c.tag_ids.includes(tagFilter)) return false;
      return true;
    });
  }, [contacts, search, tagFilter]);

  const totals = useMemo(() => {
    const withEmail = contacts.filter((c) => Boolean(c.email)).length;
    const withPhone = contacts.filter((c) => Boolean(c.phone)).length;
    return { withEmail, withPhone };
  }, [contacts]);

  const hasActiveFilters = Boolean(search) || tagFilter !== 'all';
  const clear = (): void => {
    setSearch('');
    setTagFilter('all');
  };

  return (
    <div className="space-y-6">
      <AddContactDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        trigger={<span className="hidden" />}
        onAdded={() => setRefreshTick((t) => t + 1)}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          label="Contacts"
          icon={Users}
          primary={{ label: 'Total', value: contacts.length }}
          secondary={{ label: 'Showing', value: filtered.length }}
        />
        <KpiCard
          label="Reachable"
          icon={Mail}
          primary={{ label: 'With email', value: totals.withEmail }}
          secondary={{ label: 'With phone', value: totals.withPhone }}
        />
        <KpiCard
          label="Tags"
          primary={{ label: 'Defined', value: tags.length }}
        />
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search contacts' }}
        filters={[
          <select
            key="tag"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            aria-label="Filter by tag"
            className="h-9 rounded-md border border-border bg-surface px-2 text-sm shadow-sm dark:bg-surface-dark dark:border-border"
          >
            <option value="all">All tags</option>
            {tags.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>,
        ]}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
      />

      {error ? (
        <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">
          {error}
        </div>
      ) : null}

      {loading ? (
        <Card>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 rounded-md" />
            ))}
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title={hasActiveFilters ? 'No matches' : 'No contacts yet'}
          description={
            hasActiveFilters
              ? 'Try clearing filters to see all contacts.'
              : 'Add vets, suppliers, the county clerk — anyone your ranch needs to reach.'
          }
          action={
            hasActiveFilters ? (
              <Button onClick={clear} variant="outline">
                Clear filters
              </Button>
            ) : (
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" />
                Add contact
              </Button>
            )
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-surface dark:bg-surface-dark dark:border-border">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-surface-raised text-left dark:bg-surface-raised-dark">
              <tr>
                <Th>Name</Th>
                <Th>Company</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Tags</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ');
                return (
                  <tr
                    key={c.id}
                    className="border-b border-border last:border-0 hover:bg-surface-raised dark:hover:bg-surface-raised-dark"
                  >
                    <Td className="font-medium">
                      {fullName || <span className="text-muted-foreground">—</span>}
                    </Td>
                    <Td>{c.company}</Td>
                    <Td className="text-muted-foreground">
                      {c.email ? (
                        <a
                          href={`mailto:${c.email}`}
                          className="inline-flex items-center gap-1 hover:underline"
                        >
                          <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          {c.email}
                        </a>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td className="font-mono text-xs text-muted-foreground">
                      {c.phone ? (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          {c.phone}
                        </span>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td>
                      <div className="flex flex-wrap gap-1">
                        {c.tag_ids.length === 0 ? (
                          <span className="text-2xs text-muted-foreground">—</span>
                        ) : (
                          c.tag_ids.map((id) => {
                            const tag = tagsById.get(id);
                            return (
                              <span
                                key={id}
                                className="inline-flex rounded-full bg-olive/15 px-2 py-0.5 font-mono text-2xs uppercase tracking-wider text-olive dark:bg-olive-dark/20 dark:text-olive-dark"
                              >
                                {tag?.name ?? id}
                              </span>
                            );
                          })
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: ReactNode }): JSX.Element {
  return (
    <th className="px-4 py-2 font-mono text-2xs uppercase tracking-wider text-muted-foreground">
      {children}
    </th>
  );
}

function Td({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return <td className={`px-4 py-3 ${className ?? ''}`}>{children}</td>;
}
